import { useMutation, useQueryClient } from '@tanstack/react-query'

import { useAuthInfo } from '@sweetauth/core'

import { API_URL } from '@/constants/env'

import {
  bookmarkCheckQueryKeys,
  bookmarksQueryKeys,
} from '@/hooks/bookmarks/types'
import { useHandleError } from '@/hooks/common/useErrorHandler'

import type {
  Bookmark,
  BookmarkCheck,
  CreateBookmarkRequest,
} from '@/types/bookmark/Bookmark'
import type { UUID } from '@/types/Id'

interface UseCreateBookmarkProps {
  orgId: UUID | ''
}

const createBookmark = async (
  orgId: UUID | '',
  req: CreateBookmarkRequest,
  accessToken: string | undefined | null,
): Promise<Bookmark> => {
  if (accessToken === null || accessToken === undefined || !orgId) {
    throw new Error('No access token or org ID provided.')
  }

  const res = await fetch(
    `${API_URL}/api/v1/organizations/${orgId}/bookmarks`,
    {
      method: 'POST',
      body: JSON.stringify(req),
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
    },
  )

  if (!res.ok) {
    throw new Error(res.statusText)
  }

  return (await res.json()) as Bookmark
}

export const useCreateBookmark = ({ orgId }: UseCreateBookmarkProps) => {
  const { accessToken } = useAuthInfo()
  const { handleError } = useHandleError()
  const queryClient = useQueryClient()

  return useMutation<Bookmark, Error, CreateBookmarkRequest>({
    mutationFn: (req) => createBookmark(orgId, req, accessToken),
    onError: (err) => {
      handleError(err)
    },
    onSuccess: (data, req) => {
      // Invalidate bookmarks list
      queryClient.invalidateQueries({
        queryKey: bookmarksQueryKeys.organization({ orgId }),
      })

      // Set the bookmark check cache directly (faster than invalidating)
      const checkKey = bookmarkCheckQueryKeys.check({
        orgId,
        bookmarkableType: req.bookmarkable_type,
        objectId: req.object_id,
      })
      const checkData: BookmarkCheck = {
        is_bookmarked: true,
        bookmark_id: data.id,
      }
      queryClient.setQueryData(checkKey, checkData)
    },
  })
}
